import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import { createAuth0 } from '@auth0/auth0-vue'
import { Quasar, Notify, Dialog, AppFullscreen } from 'quasar'
import { QMediaPlayer } from '@quasar/quasar-ui-qmediaplayer'
import QMediaPlayerLangEnUS from '@quasar/quasar-ui-qmediaplayer/src/components/lang/en-US.js'
import quasarIconSet from 'quasar/icon-set/svg-mdi-v6'
import {
  mdiPlay,
  mdiPause,
  mdiVolumeOff,
  mdiVolumeMedium,
  mdiVolumeHigh,
  mdiCog,
  mdiRun,
  mdiClosedCaption,
  mdiCheck,
  mdiFullscreen,
  mdiFullscreenExit
} from '@quasar/extras/mdi-v6'
import VueMatomo from 'vue-matomo'

// Styles
import '@quasar/extras/roboto-font/roboto-font.css'
import '@quasar/extras/mdi-v6/mdi-v6.css'
import '@quasar/quasar-ui-qmediaplayer/src/index.sass'
import 'quasar/src/css/index.sass'
import './styles/layout.sass'
import './styles/animation.sass'

// Animations
// import @quasar/extras/animate/fadeIn.css
// import @quasar/extras/animate/fadeOut.css

// App
import App from './App.vue'
import router from './router'
import logger from './logger'
import { useSessionStore } from './stores/session/session.store'
import { createE2eAuth0 } from './e2e/fake-auth0'

// Constants
const HEARTBEAT_INTERVAL_MS = 30000

// Configure app
const app = createApp(App)

// Configure authentication.
// MODE is replaced with a constant at build time, so outside `vite build --mode e2e` the fake auth
// branch (and its import) is removed from the bundle. Never gate this on a runtime variable.
if (import.meta.env.MODE === 'e2e') {
  app.use(createE2eAuth0())
} else {
  app.use(
    createAuth0({
      domain: import.meta.env.VITE_AUTH_CLIENT_DOMAIN,
      clientId: import.meta.env.VITE_AUTH_CLIENT_ID,
      authorizationParams: {
        redirect_uri: window.location.origin,
        audience: import.meta.env.VITE_AUTH_AUDIENCE,
        scope: 'openid profile email phone picture offline_access'
      },
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      useRefreshTokensFallback: true
    })
  )
}

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
app.use(pinia)
app.use(router)
app.use(Quasar, {
  plugins: {
    Notify,
    Dialog,
    AppFullscreen
  },
  iconSet: quasarIconSet
})
if (import.meta.env.VITE_ANALYTICS_URL && import.meta.env.VITE_ANALYTICS_SITE_ID) {
  app.use(VueMatomo, {
    host: import.meta.env.VITE_ANALYTICS_URL,
    siteId: import.meta.env.VITE_ANALYTICS_SITE_ID,
    router: router
  })
}
app.component('QMediaPlayer', QMediaPlayer)

// QMediaPlayer uses dynamic bare-module-specifier imports that browsers can't
// resolve without an import map. Pre-load lang/icon-set so the library's own
// UMD fallback path is taken instead (checks window.QMediaPlayer.Component).
;(window as any).QMediaPlayer = {
  Component: true,
  lang: { enUS: QMediaPlayerLangEnUS },
  iconSet: {
    svgMdiV6: {
      name: 'svg-mdi-v6',
      mediaPlayer: {
        play: mdiPlay,
        pause: mdiPause,
        volumeOff: mdiVolumeOff,
        volumeDown: mdiVolumeMedium,
        volumeUp: mdiVolumeHigh,
        settings: mdiCog,
        speed: mdiRun,
        language: mdiClosedCaption,
        selected: mdiCheck,
        fullscreen: mdiFullscreen,
        fullscreenExit: mdiFullscreenExit,
        bigPlayButton: mdiPlay
      }
    }
  }
}

app.mount('#app')
logger.debug('App mounted')

const sessionStore = useSessionStore()
sessionStore
  .heartbeat()
  .then(() => {
    if (!sessionStore.isOnline) {
      logger.error('Heartbeat failed. Not connected with API')
      router.push({ name: 'error' })
      return
    }
    logger.debug('Heartbeat succeeded. Connected with API')
    // Poll for heartbeat every 30 seconds
    setInterval(() => sessionStore.heartbeat(), HEARTBEAT_INTERVAL_MS)
  })
  .catch((e) => {
    logger.error('Heartbeat failed', e)
    router.push({ name: 'error' })
  })
